<script>
import api from '@/helpers/api'
import moment from 'moment'
import { chain } from 'lodash'
import VueApexCharts from 'vue-apexcharts'
import {
  isDashboardDev,
  modelTypeStatsDev
} from '@/router/views/dashboards/shared/dev-data'

var modelTypes = [
  'full_seat',
  'full_mattress',
  'seat_only',
  'back_only',
  'relax',
  'low_abduction',
  'acto',
  'assmann',
  'assmann_high',
  'hq_matrix'
]

export default {
  name: 'stats.qty.charts.app',
  components: { apexchart: VueApexCharts },
  props: {
    year: {
      type: Number,
      default: null
    },
    showYearNav: {
      type: Boolean,
      default: true
    },
    showHeading: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isLoaded: false,
      statData: [],
      series: [],
      localYear: moment().year(),
      minYear: moment().year(),
      colorMappings: {},
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: {
            show: false
          }
        }
      }
    }
  },
  computed: {
    activeYear () {
      return this.year != null ? this.year : this.localYear
    }
  },
  watch: {
    activeYear: 'onRefresh'
  },
  async mounted () {
    var colorMappings = {}

    var colors = [
      { value: 'rgba(230, 55, 55, 1)' },
      { value: 'rgba(18, 165, 188, 1)' },
      { value: 'rgba(164, 214, 48, 1)' },
      { value: 'rgba(214, 169, 62, 1)' },
      { value: 'rgba(229, 94, 19, 1)' },
      { value: 'rgba(17, 119, 225, 1)' },
      { value: 'rgba(163, 12, 193, 1)' },
      { value: 'rgba(41, 189, 115, 1)' },
      { value: 'rgba(100, 130, 20, 1)' },
      { value: 'rgba(23, 72, 170, 1)' }
    ]

    for (let index = 0; index < modelTypes.length; index++) {
      colorMappings[this.$t(`attribute.model_type.${modelTypes[index]}`)] =
        colors[index].value
    }

    this.colorMappings = colorMappings

    this.chartOptions = {
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: {
          show: false
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0
            }
          }
        }
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '80%',
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              offsetX: 0,
              style: {
                fontSize: '13px',
                fontWeight: 900
              }
            }
          }
        }
      },
      yaxis: {
        title: {
          text: this.$t('Quantities')
        }
      },
      xaxis: {
        title: {
          text: this.$t('Months')
        },
        type: 'category',
        tickPlacement: 'on',
        tickAmount: 'dataPoints'
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          colors: ['#fff']
        },
        total: {
          show: true,
          offsetY: 0,
          style: {
            fontSize: '10px',
            colors: ['#000']
          }
        }
      },
      legend: {
        position: 'right',
        offsetY: 40,
        labels: {
          useSeriesColors: false
        }
      },
      fill: {
        opacity: 0.9
      }
    }

    try {
      this.minYear = await api.webForms.fetchMinStatsYear()
    } catch (e) {
      this.minYear = moment().year() - 2
    }
    if (this.year == null) this.localYear = moment().year()
    this.onRefresh()
  },
  methods: {
    nextYear () {
      if (this.localYear < moment().year()) this.localYear++
    },
    previousYear () {
      if (this.localYear > this.minYear) this.localYear--
    },
    async onRefresh () {
      this.isLoaded = false
      let statData = []
      try {
        statData = await api.webForms.fetchModelTypeStats({
          year: this.activeYear
        })
      } catch (e) {
        statData = []
      }

      const liveQty = chain(statData || [])
        .sumBy(x => x.quantity || 0)
        .value()

      if (isDashboardDev() && !liveQty) {
        this.statData = modelTypeStatsDev.map(row => ({
          ...row,
          year: this.activeYear
        }))
      } else {
        this.statData = statData || []
      }

      this.setChartData()
      this.isLoaded = true
    },
    setChartData () {
      var months = Array.from({ length: 12 }, (_, i) => i + 1)

      this.series = chain(this.statData)
        .filter(x => x.model_type !== null)
        .groupBy('model_type')
        .map((value, model_type) => {
          if (model_type === null) return null
          var data = []

          months.forEach(month => {
            var qty = chain(
              value.filter(
                x => month === x.month && Number(x.year) === Number(this.activeYear)
              )
            )
              .sumBy(x => x.quantity)
              .value()

            data = [
              ...data,
              {
                x: moment()
                  .year(this.activeYear)
                  .month(month - 1)
                  .format('MMM yyyy'),
                y: qty
              }
            ]
          })

          return {
            data,
            name: this.$t(`attribute.model_type.${model_type}`)
          }
        })
        .value()
    }
  }
}
</script>
<template>
  <div class="d-flex flex-column">
    <div
      v-if="showYearNav"
      class="d-flex justify-content-between align-items-center"
    >
      <h5 class="m-0 mr-4">{{ $t('Ordered Quantities ') }} {{ activeYear }}</h5>
      <div class="d-flex justify-content-end align-items-center">
        <b-btn
          variant="link"
          class="m-0"
          title="Previous Year"
          v-on:click="previousYear()"
          ><i class="bx bx-caret-left font-size-18"></i
        ></b-btn>
        <b-btn
          variant="link"
          class="m-0"
          title="Next Year"
          v-on:click="nextYear()"
          ><i class="bx bx-caret-right font-size-18"></i
        ></b-btn>
      </div>
    </div>
    <h5 v-else-if="showHeading" class="m-0 mb-2">
      {{ $t('Ordered Quantities ') }} {{ activeYear }}
    </h5>
    <apexchart
      height="280"
      v-show="isLoaded"
      :options="chartOptions"
      :series="series"
    ></apexchart>
  </div>
</template>
